import * as yup from 'yup';
import type { PromoDiscountType } from '../types';

const optionalNonNegativeNumberString = (label: string) =>
  yup
    .string()
    .trim()
    .test('optional-number', `${label} должна быть числом ≥ 0`, (value) => {
      if (value == null || value === '') return true;
      const n = Number(value);
      return Number.isFinite(n) && n >= 0;
    });

const optionalPositiveIntString = (label: string, min: number) =>
  yup
    .string()
    .trim()
    .test(
      'optional-int',
      min === 1
        ? `${label} должен быть целым числом ≥ 1`
        : `${label} должен быть целым числом ≥ ${min}`,
      (value) => {
        if (value == null || value === '') return true;
        const n = Number(value);
        return Number.isInteger(n) && n >= min;
      },
    );

export const promoCodeFormSchema = yup.object({
  code: yup
    .string()
    .trim()
    .required('Укажите код промокода')
    .min(1, 'Укажите код промокода')
    .max(50, 'Код не длиннее 50 символов'),
  description: yup.string().trim().max(500, 'Описание не длиннее 500 символов').default(''),
  discountType: yup
    .mixed<PromoDiscountType>()
    .oneOf(['percentage', 'fixed_amount'], 'Выберите тип скидки')
    .required('Выберите тип скидки'),
  discountValue: yup
    .number()
    .typeError('Укажите значение скидки')
    .required('Укажите значение скидки')
    .min(0, 'Значение скидки не может быть отрицательным')
    .when('discountType', {
      is: 'percentage',
      then: (schema) =>
        schema
          .max(100, 'Процент не больше 100')
          .test('gt-zero', 'Укажите процент больше 0', (v) => v == null || v > 0),
      otherwise: (schema) =>
        schema.test('gt-zero', 'Укажите сумму скидки больше 0', (v) => v == null || v > 0),
    }),
  minimumCartAmount: optionalNonNegativeNumberString('Минимальная сумма'),
  isActive: yup.boolean().required().default(true),
  usageLimitTotal: optionalNonNegativeNumberString('Лимит использований').test(
    'optional-int-total',
    'Лимит всего должен быть целым числом ≥ 0',
    (value) => {
      if (value == null || value === '') return true;
      const n = Number(value);
      return Number.isInteger(n) && n >= 0;
    },
  ),
  usageLimitPerUser: optionalPositiveIntString('Лимит на пользователя', 1),
  validFrom: yup
    .date()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue == null ? null : value,
    )
    .default(null),
  validUntil: yup
    .date()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue == null ? null : value,
    )
    .default(null)
    .test(
      'after-from',
      'Дата окончания должна быть не раньше даты начала',
      function (value) {
        const from = this.parent.validFrom as Date | null | undefined;
        if (!value || !from) return true;
        return value.getTime() >= from.getTime();
      },
    ),
  appliesToCategoryId: yup.number().nullable().default(null),
  appliesToProductIds: yup.array().of(yup.number().required()).default([]),
  excludeProductIds: yup.array().of(yup.number().required()).default([]),
});
